import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { formatDate, formatDateTime } from '../../utils/format';
import { filterMilestones, monthRange, milestonePo, REPORT_FILTER_COLUMNS } from '../../utils/milestoneReport';
import ColumnFilter from '../shared/ColumnFilter';

// Reporting view of the Milestones page: the delivery data recorded against
// each live milestone (PO and PO line item, ATV and client PMs, the SOW's RAG,
// dependencies, summary) rather than the billing actions, chosen by status and
// by forecast date and narrowed by a filter row under the headers, with an
// expandable history of date and status changes beneath each row.

const STATUSES = ['Awaiting', 'Ready to bill', 'Invoiced', 'Paid', 'Cancelled', 'All'];

const RAG_COLORS = {
  red:   { cls: 'bg-red-100 text-red-700',         dot: '#EF4444', label: 'Red'   },
  amber: { cls: 'bg-amber-100 text-amber-700',     dot: '#F59E0B', label: 'Amber' },
  green: { cls: 'bg-emerald-100 text-emerald-700', dot: '#10B981', label: 'Green' },
};

// The RAG is the SOW's, from its latest Project Update: RAG is recorded per
// project, so every milestone on a SOW shows the same one.
function RagBadge({ rag, week }) {
  const c = RAG_COLORS[(rag || '').toLowerCase()];
  if (!c) return <span className="text-gray-300" title="No project update on this SOW">—</span>;
  return (
    <span title={`SOW RAG from the project update${week ? ` w/c ${formatDate(week)}` : ''}`} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${c.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: c.dot }} />
      {c.label}
    </span>
  );
}

const FIELD_LABEL = { contract_date: 'Contract date', forecast_date: 'Forecast date', status: 'Status' };
const fmtVal = (field, v) => (!v ? '—' : field === 'status' ? v : formatDate(v));

function HistoryRows({ milestoneId, colSpan }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`milestones/${milestoneId}/rag`)
      .then(d => setRows((Array.isArray(d) ? d : []).filter(r => r._type === 'date_change' || r._type === 'status_change')))
      .catch(() => setRows([]));
  }, [milestoneId]);
  return (
    <tr className="bg-gray-50/60">
      <td colSpan={colSpan} className="px-4 py-2">
        {rows === null ? <div className="text-[9px] text-gray-400">Loading history…</div>
        : rows.length === 0 ? <div className="text-[9px] text-gray-400">No date or status changes recorded.</div>
        : (
          <table className="text-[9px]">
            <tbody>{rows.map(r => (
              <tr key={r.id}>
                <td className="pr-3 py-0.5 text-gray-500 whitespace-nowrap">{formatDateTime(r.submitted_at)}</td>
                <td className="pr-3 py-0.5 font-semibold text-navy whitespace-nowrap">{FIELD_LABEL[r.field_name] || r.field_name}</td>
                <td className="pr-3 py-0.5 whitespace-nowrap">{fmtVal(r.field_name, r.old_value)} → {fmtVal(r.field_name, r.new_value)}</td>
                <td className="py-0.5 text-gray-500 whitespace-nowrap">{r.updated_by_name}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </td>
    </tr>
  );
}

// The client's PO line item, edited in place: anyone on this page may set it,
// and it is often only known once the PO arrives, long after the SOW was set up.
function PoLineCell({ m, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue]     = useState(m.po_line_item || '');
  const [saving, setSaving]   = useState(false);
  const [failed, setFailed]   = useState('');

  const startEdit = () => { setValue(m.po_line_item || ''); setFailed(''); setEditing(true); };

  const commit = async () => {
    setEditing(false);
    const next = value.trim();
    if (next === (m.po_line_item || '')) return;
    setSaving(true);
    setFailed('');
    try {
      await api.put(`milestones/${m.id}`, { po_line_item: next || null });
      onSaved({ po_line_item: next || null });
    } catch (e) {
      setFailed(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter')  e.currentTarget.blur();
          if (e.key === 'Escape') { setValue(m.po_line_item || ''); setEditing(false); }
        }}
        placeholder="e.g. 10"
        aria-label={`PO line item for ${m.milestone_code}`}
        className="w-[64px] text-[9px] border border-navy rounded px-1 py-0.5 focus:outline-none"
      />
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={startEdit}
        title={m.po_line_item ? 'Click to edit the PO line item' : 'No PO line item recorded. Click to add it.'}
        className={`text-left rounded px-1 py-0.5 hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 ${m.po_line_item ? 'text-gray-700' : 'text-[8px] text-gray-300'}`}
      >
        {saving ? 'Saving…' : (m.po_line_item || '+ add')}
      </button>
      {failed && <div className="text-[7px] text-red-600 font-semibold" title={failed}>Save failed</div>}
    </div>
  );
}

const TH = ({ children }) => <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">{children}</th>;
const COLS = 12;
const FILTER_BY_KEY = Object.fromEntries(REPORT_FILTER_COLUMNS.map(c => [c.key, c.filter]));

// The filters are held by MilestonesPage, whose Export CSV sends the rows this
// table shows: { status, month, from, to, columns }, columns being the filter
// row's values keyed as in REPORT_FILTER_COLUMNS.
export default function MilestoneReportingTab({ milestones, filter, onFilterChange, onMilestoneSaved }) {
  const { status, month } = filter;
  const range = { from: filter.from, to: filter.to };
  const columns = filter.columns || {};
  const [open, setOpen] = useState(() => new Set());

  // The month picker is a shortcut: picking one fills the range, and editing
  // the range by hand clears the month so the two never disagree.
  const setStatus = (s) => onFilterChange({ ...filter, status: s });
  const pickMonth = (m) => onFilterChange({ ...filter, month: m, ...monthRange(m) });
  const pickRange = (r) => onFilterChange({ ...filter, month: '', ...r });

  const setColumn = (key, v) => onFilterChange({ ...filter, columns: { ...columns, [key]: v } });
  const anyColumn = Object.values(columns).some(v => v?.length);

  // The pick-lists offer what the status and date filters leave, so a PM with
  // nothing Awaiting is not offered while Awaiting is chosen.
  const inScope = filterMilestones(milestones, { ...filter, columns: {} });
  const rows = filterMilestones(milestones, filter);
  // A function, not a component: one defined in here would be a new type on
  // every render and remount, dropping the focus of a box being typed in.
  const filterCell = (k, i) => (
    <th key={k || `none-${i}`} className="px-2 py-1 border-b border-gray-200 bg-blue-50/40">
      {k && <ColumnFilter filter={FILTER_BY_KEY[k]} rows={inScope} value={columns[k]} onChange={v => setColumn(k, v)} />}
    </th>
  );
  const toggle = (id) => setOpen(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="text-[9px] text-gray-500 font-semibold">Status
          <select className="form-input block mt-0.5" value={status} onChange={e => setStatus(e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-[9px] text-gray-500 font-semibold">Forecast month
          <input type="month" className="form-input block mt-0.5" value={month} onChange={e => pickMonth(e.target.value)} />
        </label>
        <label className="text-[9px] text-gray-500 font-semibold">Forecast from
          <input type="date" className="form-input block mt-0.5" value={range.from} onChange={e => pickRange({ ...range, from: e.target.value })} />
        </label>
        <label className="text-[9px] text-gray-500 font-semibold">to
          <input type="date" className="form-input block mt-0.5" value={range.to} onChange={e => pickRange({ ...range, to: e.target.value })} />
        </label>
        {(range.from || range.to) && (
          <button className="text-[9px] text-gray-500 underline pb-2" onClick={() => pickRange({ from: '', to: '' })}>Clear dates</button>
        )}
        <div className="text-[9px] text-gray-500 pb-2 ml-auto flex items-center gap-2">
          {anyColumn && (
            <button className="text-[9px] text-blue-500 hover:underline font-semibold" onClick={() => onFilterChange({ ...filter, columns: {} })}>Clear column filters</button>
          )}
          <span>{rows.length} milestone{rows.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <TH>AVM</TH>
              <TH>PO</TH>
              <TH>PO Line</TH>
              <TH>Status</TH>
              <TH>Contract</TH>
              <TH>Forecast</TH>
              <TH>ATV PM</TH>
              <TH>Client PM</TH>
              <TH>RAG</TH>
              <TH>Dependencies</TH>
              <TH>Summary</TH>
              <TH>Changes</TH>
            </tr>
            {/* Filter row: status and forecast date are filtered above */}
            <tr>
              {['avm', 'po', 'po_line_item', null, null, null, 'atv_pm', 'client_pm', 'rag', 'dependencies', 'summary', null].map(filterCell)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={COLS} className="px-2 py-4 text-center text-[9px] text-gray-400">No milestones match.</td></tr>
            )}
            {rows.map(m => {
              const isOpen = open.has(m.id);
              const dep = [m.dependency_reason, m.dependency_comment].filter(Boolean).join(': ');
              const po = milestonePo(m);
              return [
                <tr key={m.id} className="hover:bg-gold/5 text-[9px] align-top">
                  <td className="px-2 py-1 whitespace-nowrap">
                    <div className="font-bold text-navy">{m.milestone_code}</div>
                    <div className="text-gray-500">{m.reference}{m.client_name ? ` · ${m.client_name}` : ''}</div>
                    {m.sow_detail && <div className="text-gray-400 max-w-[220px] truncate" title={m.sow_detail}>{m.sow_detail}</div>}
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap">{po || <span className="text-gray-300">No PO</span>}</td>
                  <td className="px-2 py-1 whitespace-nowrap"><PoLineCell m={m} onSaved={patch => onMilestoneSaved(m.id, patch)} /></td>
                  <td className="px-2 py-1 whitespace-nowrap">{m.status}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{formatDate(m.contract_date) || '—'}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{formatDate(m.forecast_date) || '—'}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{m.pm_name || <span className="text-gray-300">—</span>}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{m.client_contact_name || <span className="text-gray-300">—</span>}</td>
                  <td className="px-2 py-1"><RagBadge rag={m.sow_overall_rag} week={m.sow_rag_week} /></td>
                  <td className="px-2 py-1 max-w-[220px]">{dep || <span className="text-gray-300">—</span>}</td>
                  <td className="px-2 py-1 max-w-[320px] whitespace-pre-wrap">{m.summary || <span className="text-gray-300">—</span>}</td>
                  <td className="px-2 py-1 whitespace-nowrap">
                    <button className="text-gold font-semibold hover:underline" onClick={() => toggle(m.id)} aria-expanded={isOpen}>
                      {isOpen ? '▾ Hide' : '▸ Show'}
                    </button>
                  </td>
                </tr>,
                isOpen && <HistoryRows key={`${m.id}-h`} milestoneId={m.id} colSpan={COLS} />,
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
